import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styles from './PublicSiteLayout.module.css';
import { publicSiteAPI } from '../../services/api';
import { PublicSiteHeader } from './PublicSiteHeader';
import { PublicSiteFooter } from './PublicSiteFooter';
import { HeroSection } from './sections/HeroSection';
import { AboutSection } from './sections/AboutSection';
import { FactsSection } from './sections/FactsSection';
import { TimelineSection } from './sections/TimelineSection';
import { MediaGallerySection } from './sections/MediaGallerySection';
import { ParticipationSection } from './sections/ParticipationSection';
import { FAQSection } from './sections/FAQSection';
import { ContactSection } from './sections/ContactSection';
import { NewsletterSection } from './sections/NewsletterSection';
import { GebiedsanalyseSection } from './sections/GebiedsanalyseSection';

export const PublicSiteLayout: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (slug) {
      loadProject();
    }
  }, [slug]);

  const loadProject = async () => {
    try {
      const data = await publicSiteAPI.getBySlug(slug!);
      setProject(data);
      document.title = data.publicWebsite?.seo?.metaTitle || data.name;
    } catch {
      setError('Website niet gevonden');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner} />
        <p>Laden...</p>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className={styles.notFound}>
        <h1>404</h1>
        <p>Deze projectwebsite is niet gevonden.</p>
        <a href="/" className={styles.backLink}>Terug naar home</a>
      </div>
    );
  }

  const pw = project.publicWebsite;
  const sections = pw?.sections || {};
  const primaryColor = pw?.theme?.primaryColor || '#E6007E';

  const sortedSections = Object.entries(sections)
    .filter(([, config]: [string, any]) => config.enabled)
    .sort(([, a]: [string, any], [, b]: [string, any]) => (a.order ?? 99) - (b.order ?? 99));

  const sectionIds = sortedSections.map(([id]) => id);

  return (
    <div className={styles.publicSite} style={{ '--primary-color': primaryColor } as React.CSSProperties}>
      <PublicSiteHeader projectName={project.name} sections={sectionIds} />

      {sortedSections.map(([sectionId]) => {
        switch (sectionId) {
          case 'hero':
            return <HeroSection key={sectionId} project={project} />;
          case 'about':
            return <AboutSection key={sectionId} project={project} />;
          case 'facts':
            return <FactsSection key={sectionId} facts={pw?.facts || []} image={pw?.factsImage} />;
          case 'timeline':
            return <TimelineSection key={sectionId} slug={slug!} />;
          case 'media':
            return <MediaGallerySection key={sectionId} slug={slug!} />;
          case 'participation':
            return <ParticipationSection key={sectionId} participation={pw?.participation} />;
          case 'faq':
            return <FAQSection key={sectionId} slug={slug!} />;
          case 'contact':
            return <ContactSection key={sectionId} project={project} slug={slug!} />;
          case 'newsletter':
            return <NewsletterSection key={sectionId} newsletter={pw?.newsletter} slug={slug!} />;
          case 'gebiedsanalyse':
            return <GebiedsanalyseSection key={sectionId} slug={slug!} />;
          default:
            return null;
        }
      })}

      <PublicSiteFooter projectName={project.name} />
    </div>
  );
};
